/**
 * Settings — chrome, not a pane.
 *
 * It configures the window, so it opens over the window (SettingsModal) rather
 * than inside the content tree. That is also what makes it simple: there is
 * only ever one, it is never split, and it does not need a place in an
 * arrangement.
 *
 * ARRANGEMENT is offered as SHAPES rather than named layouts. Panes hold real
 * sessions and documents, so a preset cannot carry its own content the way the
 * old panel presets did: "Columns" means lay out what is open.
 *
 * LAYOUTS are the other thing, and the two are not the same word by accident.
 * An arrangement is how the panes you have are laid out right now. A layout
 * (RFC 0284 §4e) is an installed record saying which fill fills each
 * chrome slot, and the space you are in names one. This is where a person picks
 * between them, and it is the only place the whole of RFC 0284 is visible.
 */

import {
  MOD_SKILLS,
  type ModSkill,
  type SettingSpec,
  type SettingValue,
} from '@mod/core/model'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  ARRANGE_PRESETS,
  type ArrangePreset,
  type ArrangeShape,
} from '../../features/code/contentTree'
import { useSharingAccount } from '../../services/sharing/accountContext'
import { Panel, PanelAction, PanelLabel } from './Panel'
import { RemindersList } from './RemindersSection'
import type { Chrome } from './useLayout'
import { KEYMAP } from './useProjectKeymap'

/**
 * One type, and what can open it (RFC 0284 S6m).
 *
 * `chosen` is undefined for the built-in, which covers both "they picked the
 * built-in" and "nobody picked and several claim it". The panel does not need
 * to tell those apart to draw the right selection; `source` is there so it can
 * say when the second one is why.
 */
export interface OpenWithRow {
  /** The mime or content kind, e.g. `text/markdown` or `agent`. */
  key: string
  /** In a person's words: "Sessions", "Markdown". */
  label: string
  options: { id: string; name: string }[]
  chosen?: string
  source: 'chosen' | 'only' | 'builtin'
}

/** One installed app's settings, as the panel needs them (RFC 0284 S6g). */
export interface AppSettingsSection {
  appId: string
  name: string
  /** The declared spec, plus what it is SET to. `value` absent means nobody
   *  changed it, which is what makes clearing a field mean "back to the
   *  default" rather than "empty". */
  fields: (SettingSpec & { value?: SettingValue })[]
}

export function SettingsPanel({
  arrangement,
  chrome,
  onArrange,
  onToggleSidebar,
  onToggleSpacesAsTabs,
  layouts,
  currentLayoutId,
  onPickLayout,
  onMakeLayout,
  slotSections,
  panes,
  openWith,
  onSetHandler,
  appSettings,
  onSetAppSetting,
  worldName = 'the World agent',
  onClose,
}: {
  /** What the current arrangement is called, or "Custom" once dragged. */
  arrangement: string
  chrome: Chrome
  onArrange: (preset: ArrangePreset) => void
  onToggleSidebar: () => void
  /** Where the spaces list is drawn (RFC 0281 S5a). Optional so a host without
   *  spaces renders the pane it always did. */
  onToggleSpacesAsTabs?: () => void
  /** Installed layouts (RFC 0284 §4e). Optional, so a host with no World
   *  renders the settings it always did rather than an empty section. */
  layouts?: { id: string; name: string; description: string }[]
  /** What the current space names, or null for the built-in default. */
  currentLayoutId?: string | null
  onPickLayout?: (layoutId: string | null) => void
  onMakeLayout?: () => void
  /**
   * One section per configurable slot (RFC 0284 S5, S6c).
   *
   * A LIST, not a field per slot. The sidebar had four props of its own, and
   * the header would have been four more spelled the same way; the panel would
   * then have grown a slot's worth of props every time a surface became
   * configurable. What a slot needs here is what any slot needs: what can fill
   * it, what does, and how to change that.
   *
   * Empty, or absent, renders no sections at all, which is what a host with no
   * World should see.
   */
  slotSections?: SlotSectionProps[]
  /**
   * The panes this layout opens a space on (RFC 0284 S6h).
   *
   * Beside the slots rather than a section of its own, because it answers the
   * same question they do: what this layout opens on.
   */
  panes?: {
    /** Whether the layout has any. False means it opens on the picker. */
    saved: boolean
    /** `true` saves what is on screen; `false` clears back to the picker. */
    onSave: (save: boolean) => void
    /** Replace THIS space's panes with the layout's. */
    onApply: () => void
  }
  /**
   * What each installed app can be configured to do (RFC 0284 S6g).
   *
   * The rest of the VS Code comparison. Apps says what a package IS; this is
   * where you change what it DOES, which is the split Settings exists for.
   * Only apps that DECLARE settings appear, so a World full of packages that
   * configure nothing shows nothing rather than a list of empty headings.
   */
  /**
   * Which package opens each type (RFC 0284 S6m).
   *
   * Desktop's "Open With", and the reason it exists is that an installed thread
   * was a one-way door: the only way back to the shipped one was uninstalling.
   * Absent, or empty, when nothing installed claims any type, which is every
   * World that has installed no editor and no thread.
   */
  openWith?: OpenWithRow[]
  onSetHandler?: (key: string, appId: string | undefined) => void
  appSettings?: AppSettingsSection[]
  onSetAppSetting?: (
    appId: string,
    key: string,
    value: SettingValue | undefined,
  ) => void
  /** The World agent's name, for the keys that reach it. */
  worldName?: string
  onClose: () => void
}) {
  // The window's settings, what teaches the agent, what runs it, every key
  // (RFC 0348 3f), and, when sharing is on, who is signed in (RFC 0345).
  const account = useSharingAccount()
  const [picked, setTab] = useState<SettingsTabId>('window')
  const tab = picked === 'account' && !account ? 'window' : picked
  const tabs: [SettingsTabId, string][] = [
    ['window', 'Window'],
    ['skills', 'Skills'],
    ['harness', 'Harness'],
    ['shortcuts', 'Shortcuts'],
    ...(account ? [['account', 'Account'] as [SettingsTabId, string]] : []),
  ]
  return (
    <Panel
      framed={false}
      padded={false}
      header={
        <>
          <PanelLabel>Settings</PanelLabel>
          <div className="ml-2 flex items-center gap-0.5">
            {tabs.map(([id, label]) => (
              <SettingsTab
                key={id}
                active={tab === id}
                onClick={() => setTab(id)}
              >
                {label}
              </SettingsTab>
            ))}
          </div>
          <div className="flex-1" />
          <PanelAction title="Close settings (⌘,)" onClick={onClose}>
            <X size={14} />
          </PanelAction>
        </>
      }
    >
      {tab === 'shortcuts' ? (
        <div className="h-full overflow-y-auto px-3 py-2">
          <Shortcuts worldName={worldName} />
        </div>
      ) : tab === 'skills' ? (
        <div className="h-full overflow-y-auto px-3 py-2">
          {/* RFC 0334 3g: how an agent is taught Mod itself. The agent loads
            these on its own, so they are read here rather than offered in
            `/`; a World or project skill with the same id replaces one. */}
          <Section
            title="Mod skills"
            note="How the agent is taught Mod. It loads these itself. A World or project skill with the same id replaces one."
          >
            <div className="space-y-1">
              {MOD_SKILLS.map((m) => (
                <ModSkillRow key={m.skillId} skill={m} />
              ))}
            </div>
          </Section>
        </div>
      ) : tab === 'harness' ? (
        <div className="h-full overflow-y-auto px-3 py-2">
          <Section
            title="Harness"
            note="Not wired here yet. Set on the device that hosts the worker."
          >
            <ReadOnlyRow label="Agent" value="Claude Code" />
          </Section>
        </div>
      ) : tab === 'account' && account ? (
        <div className="h-full overflow-y-auto px-3 py-2">
          <AccountSection
            email={account.account.email}
            onSignOut={() => {
              account.client.signOut()
              window.location.assign('/')
            }}
          />
        </div>
      ) : (
        <div className="h-full space-y-5 overflow-y-auto px-3 py-2">
          <Section
            title="Arrangement"
            note={`Currently ${arrangement}. Drag a pane by its header to rearrange, or split from a pane's menu.`}
          >
            <div className="space-y-1">
              {ARRANGE_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => onArrange(preset)}
                  className={`flex w-full items-center gap-2 rounded border px-2 py-1.5 text-left ${
                    preset.name === arrangement
                      ? 'border-[color:var(--ws-border-focus)] bg-muted/60'
                      : 'border-border hover:bg-muted/40'
                  }`}
                >
                  <ShapeThumbnail shape={preset.shape} />
                  <span className="flex flex-col">
                    <span className="font-medium text-[12px] text-foreground">
                      {preset.name}
                    </span>
                    <span className="text-[11px] text-muted-foreground leading-snug">
                      {preset.description}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </Section>

          {onPickLayout && (
            <Section
              title="Layout"
              note="Which chrome this space uses. Installed layouts follow you to your other machines."
            >
              <div className="space-y-1">
                <LayoutRow
                  name="Built in"
                  description="The sidebar Mod ships with."
                  selected={!currentLayoutId}
                  onClick={() => onPickLayout(null)}
                />
                {(layouts ?? []).map((l) => (
                  <LayoutRow
                    key={l.id}
                    name={l.name}
                    description={l.description || 'No description.'}
                    selected={currentLayoutId === l.id}
                    onClick={() => onPickLayout(l.id)}
                  />
                ))}
              </div>
              {onMakeLayout && (
                <button
                  type="button"
                  onClick={onMakeLayout}
                  className="mt-1 w-full rounded border border-border/60 border-dashed px-2 py-1 text-[12px] text-muted-foreground hover:bg-muted/40"
                >
                  New layout
                </button>
              )}

              {/**
               * THE SLOTS, INSIDE THE LAYOUT THAT OWNS THEM.
               *
               * These were sibling sections called "Sidebar" and "Header", which
               * read as two more categories of setting. They are not: they are
               * what the SELECTED layout puts in each slot, and a person editing
               * them is editing that layout. Indented under it so the panel says
               * so, rather than leaving the reader to infer which layout a
               * change lands in.
               *
               * ONLY WITH ONE SELECTED, because editing what the built-in
               * default puts in a slot would mean writing a layout nobody chose
               * and then silently switching to it.
               */}
              {currentLayoutId && (slotSections ?? []).length > 0 && (
                <div className="mt-2 space-y-3 border-border/60 border-l pl-3">
                  {(slotSections ?? []).map((section) => (
                    <SlotSection key={section.slot} {...section} />
                  ))}
                  {panes && (
                    /**
                     * PANES, beside the slots, because they are the same
                     * question: what this layout opens on.
                     *
                     * SAVED FROM WHAT IS ON SCREEN rather than edited here. A
                     * person arranges panes by dragging them, so the arrangement
                     * they want is already in front of them; a pane editor in a
                     * 288px column would be a worse way to say the same thing.
                     */
                    <Section
                      title="Panes"
                      note={
                        panes.saved
                          ? 'What a new space using this layout opens on.'
                          : 'This layout opens a new space on the pane picker.'
                      }
                    >
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => panes.onSave(true)}
                          className="flex-1 rounded border border-border/60 px-2 py-1 text-[12px] text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                        >
                          Save what is open
                        </button>
                        {panes.saved && (
                          <button
                            type="button"
                            onClick={() => panes.onSave(false)}
                            className="shrink-0 rounded border border-border/60 px-2 py-1 text-[12px] text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      {panes.saved && (
                        <button
                          type="button"
                          onClick={panes.onApply}
                          className="mt-1 w-full rounded border border-border/60 border-dashed px-2 py-1 text-[12px] text-muted-foreground hover:bg-muted/40"
                        >
                          Open them in this space
                        </button>
                      )}
                      {/* SWITCHING A LAYOUT DOES NOT DO THIS, said out loud,
                      because a person who just switched will wonder why their
                      panes did not move. Throwing away what somebody has open
                      as a side effect of changing chrome would be far worse
                      than making them ask. */}
                      <p className="mt-1 px-2 text-[11px] text-muted-foreground leading-snug">
                        Switching layouts keeps the panes you have open. This
                        replaces them.
                      </p>
                    </Section>
                  )}
                </div>
              )}
            </Section>
          )}

          {(openWith ?? []).length > 0 && (
            /**
             * OPEN WITH (RFC 0284 S6m), the desktop idea by the desktop name.
             *
             * One row per type anything installed claims, and "Built in" is a
             * real choice rather than a report. Before this, the only way back
             * from an installed thread was uninstalling it, which made trying one
             * a one-way door.
             */
            <Section
              title="Open with"
              note="Which package opens each kind of thing. Applies everywhere, not just this space."
            >
              <div className="space-y-3">
                {(openWith ?? []).map((row) => (
                  <div key={row.key} className="space-y-1">
                    <div className="flex items-baseline justify-between gap-2 px-0.5">
                      <span className="font-medium text-[11px] text-foreground">
                        {row.label}
                      </span>
                      {/* AMBIGUOUS, said out loud. Several claimants and no
                      choice draws the BUILT-IN rather than guessing, and a
                      person who installed something and sees the shipped one
                      would otherwise think the install failed. */}
                      {row.source === 'builtin' && row.options.length > 1 && (
                        <span className="text-[10px] text-muted-foreground">
                          pick one
                        </span>
                      )}
                    </div>
                    <LayoutRow
                      name="Built in"
                      description="The one Mod ships."
                      selected={!row.chosen}
                      onClick={() => onSetHandler?.(row.key, '')}
                    />
                    {row.options.map((option) => (
                      <LayoutRow
                        key={option.id}
                        name={option.name}
                        description="Installed."
                        selected={row.chosen === option.id}
                        onClick={() => onSetHandler?.(row.key, option.id)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {(appSettings ?? []).map((app) => (
            <AppSettingsSectionView
              key={app.appId}
              section={app}
              onSet={onSetAppSetting}
            />
          ))}

          {/* RFC 0350 S3: what the World will wake an agent for, and when. */}
          <Section
            title="Reminders"
            note="Set by asking an agent. Reminders run only while a desktop is awake. A late one-time reminder runs when it wakes. Missed repeating reminders are skipped by default; you can choose to run the latest missed one once when it wakes."
          >
            <RemindersList />
          </Section>

          <Section title="Chrome" note="The furniture around your panes.">
            <button
              type="button"
              onClick={onToggleSidebar}
              className="flex w-full items-baseline justify-between rounded border border-border/60 px-2 py-1 text-[12px]"
            >
              <span className="text-muted-foreground">Files sidebar</span>
              <span className="text-foreground">
                {chrome.sidebar ? 'Shown' : 'Hidden'}
              </span>
            </button>
            {onToggleSpacesAsTabs && (
              <button
                type="button"
                onClick={onToggleSpacesAsTabs}
                className="flex w-full items-baseline justify-between rounded border border-border/60 px-2 py-1 text-[12px]"
              >
                <span className="text-muted-foreground">Spaces</span>
                <span className="text-foreground">
                  {chrome.spacesAsTabs ? 'Tabs on top' : 'In the sidebar'}
                </span>
              </button>
            )}
          </Section>
        </div>
      )}
    </Panel>
  )
}

/**
 * What one configurable slot is (RFC 0284 S6c).
 *
 * `fill` carries all three states the layout distinguishes and the section
 * shows all three: `undefined` selects Built in, `[]` selects None, and a list
 * selects a fill. Collapsing the first two here would make "I want no
 * sidebar" unreachable from the only place a person can reach it.
 */
export interface SlotSectionProps {
  slot: string
  title: string
  /** What this surface IS, in the person's words, since "slot" is ours. */
  note: string
  /** What the shipped one is, said plainly: "the sidebar Mod ships with". */
  builtinDescription: string
  /** What emptying it does, and how to undo that. */
  noneDescription: string
  fills: { id: string; name: string }[]
  fill: readonly string[] | undefined
  onSet: (fill: string[] | undefined) => void
  /**
   * How the surface is SHAPED, when the shell can honour it (RFC 0284 S6d).
   *
   * Optional per section rather than per slot in the type, because a slot the
   * shell lays out itself has nothing to offer here yet. Passing it is the
   * shell saying "I obey this for this surface", which is a claim only it can
   * make.
   */
  geometry?: {
    size?: number
    reveal: 'pinned' | 'hover'
    /** e.g. "Height". The word for the axis this surface is measured on. */
    sizeLabel: string
    /** What it opens at when nothing is set, shown as the placeholder. */
    defaultSize: number
    onSet: (next: { size?: number; reveal?: 'pinned' | 'hover' }) => void
  }
}

/**
 * One slot of the selected layout: what can fill it, what does, and its shape.
 *
 * ONE COMPONENT for every slot, which is the test S6c exists to run. If the
 * header had needed a section of its own shape, then "a slot" would be a word
 * covering two different things and the registry underneath it would be
 * pretending.
 *
 * NO REMOVE AND NO INSTALL, since S6e. Both were here and both were the wrong
 * scope: they act on the CATALOG, not on this layout, so a person removing a
 * sidebar from inside the sidebar's section was uninstalling it everywhere
 * while appearing to edit one arrangement. They live in Installed now.
 */
function SlotSection({
  title,
  note,
  builtinDescription,
  noneDescription,
  fills,
  fill,
  onSet,
  geometry,
}: SlotSectionProps) {
  return (
    <Section title={title} note={note}>
      <div className="space-y-1">
        <LayoutRow
          name="Built in"
          description={builtinDescription}
          // SILENCE, not the built-in's id. A layout that says nothing about a
          // slot keeps working when the built-in is renamed, and reads
          // correctly in a World written before layouts existed.
          selected={fill === undefined}
          onClick={() => onSet(undefined)}
        />
        {fills.map((c) => (
          <LayoutRow
            key={c.id}
            name={c.name}
            description="Installed. Mounted in the host realm."
            selected={fill?.[0] === c.id}
            onClick={() => onSet([c.id])}
          />
        ))}
        <LayoutRow
          name="None"
          description={noneDescription}
          selected={Array.isArray(fill) && fill.length === 0}
          onClick={() => onSet([])}
        />
      </div>
      {geometry && (
        /**
         * SHAPE, below what fills it, because it is the second question.
         *
         * A person picks a sidebar and then decides how wide it is, never the
         * other way round, and putting the two in one row would make the
         * common case (pick a thing) share space with the rare one.
         */
        <div className="mt-2 space-y-1">
          <label className="flex items-baseline justify-between gap-2 rounded border border-border/60 px-2 py-1 text-[12px]">
            <span className="text-muted-foreground">{geometry.sizeLabel}</span>
            <input
              type="number"
              min={16}
              max={400}
              value={geometry.size ?? ''}
              placeholder={String(geometry.defaultSize)}
              onChange={(e) => {
                const raw = e.target.value.trim()
                // EMPTY MEANS DEFAULT, not zero. Clearing the box has to be
                // the way back, or a person who typed a number is stuck with
                // having an opinion forever.
                const size = raw === '' ? undefined : Number(raw)
                geometry.onSet({
                  size: size !== undefined && size > 0 ? size : undefined,
                  reveal: geometry.reveal,
                })
              }}
              className="w-16 rounded border border-border/60 bg-transparent px-1 text-right text-foreground"
            />
          </label>
          <button
            type="button"
            onClick={() =>
              geometry.onSet({
                size: geometry.size,
                reveal: geometry.reveal === 'hover' ? 'pinned' : 'hover',
              })
            }
            className="flex w-full items-baseline justify-between rounded border border-border/60 px-2 py-1 text-[12px]"
          >
            <span className="text-muted-foreground">Show</span>
            <span className="text-foreground">
              {geometry.reveal === 'hover' ? 'On hover' : 'Always'}
            </span>
          </button>
          {geometry.reveal === 'hover' && (
            <p className="px-2 text-[11px] text-muted-foreground leading-snug">
              The panes get the space. Move the pointer to the edge to bring it
              back.
            </p>
          )}
        </div>
      )}
    </Section>
  )
}

/**
 * One layout to choose from, including the built-in.
 *
 * The built-in is a ROW like any other rather than an "off" switch, because
 * going back to it is a choice a person makes as often as leaving it, and a
 * layout that empties the sidebar leaves nothing else to click.
 */
function LayoutRow({
  name,
  description,
  selected,
  onClick,
}: {
  name: string
  description: string
  selected: boolean
  /** Absent makes the row a REPORT rather than a choice, for a state that is
   *  reached by removing something rather than by picking it (S6b's thread). */
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`flex w-full flex-col items-start rounded border px-2 py-1.5 text-left ${
        selected
          ? 'border-[color:var(--ws-border-focus)] bg-muted/60'
          : 'border-border hover:bg-muted/40'
      }`}
    >
      <span className="font-medium text-[12px] text-foreground">{name}</span>
      <span className="text-[11px] text-muted-foreground leading-snug">
        {description}
      </span>
    </button>
  )
}

/**
 * Settings opens over the window, like Packages, rather than as a column
 * beside the panes: five tabs did not fit in 288px. Escape or a click
 * outside closes it.
 */
export function SettingsModal({
  onClose,
  children,
}: {
  onClose: () => void
  children: React.ReactNode
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      onClose()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose])
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
      <button
        type="button"
        aria-label="Close settings"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-foreground/10"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        className="relative flex h-[70vh] w-full max-w-2xl flex-col overflow-hidden rounded-[var(--radius-project)] border border-border bg-background shadow-lg"
      >
        {children}
      </div>
    </div>
  )
}

type SettingsTabId = 'window' | 'skills' | 'harness' | 'shortcuts' | 'account'

/**
 * Who this window is signed in as (RFC 0345). Signing out forgets the
 * sign-in, not this browser's keys, so the same account opens again after
 * signing back in.
 */
function AccountSection({
  email,
  onSignOut,
}: {
  email: string
  onSignOut: () => void
}) {
  return (
    <Section title="Account" note="Signing out keeps this browser's keys.">
      <ReadOnlyRow label="Signed in as" value={email} />
      <button
        type="button"
        onClick={onSignOut}
        className="mt-1 w-full rounded border border-border/60 px-2 py-1 text-[12px] text-muted-foreground hover:bg-muted/40 hover:text-foreground"
      >
        Sign out
      </button>
    </Section>
  )
}

function SettingsTab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded px-1.5 py-0.5 text-[12px] ${
        active
          ? 'bg-muted text-foreground'
          : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  )
}

/** Every key the window binds, read off the same table the listener is
 *  tested against, so the list cannot fall behind the keys. */
function Shortcuts({ worldName }: { worldName: string }) {
  return (
    <ul className="space-y-0.5">
      {KEYMAP.map((k) => (
        <li
          key={k.label}
          className="flex items-baseline justify-between gap-3 py-1 text-[12px]"
        >
          <span className="text-foreground">
            {k.action.replace('{world}', worldName)}
          </span>
          <kbd className="shrink-0 font-sans text-muted-foreground">
            {k.label}
          </kbd>
        </li>
      ))}
    </ul>
  )
}

function Section({
  title,
  note,
  children,
}: {
  title: string
  note?: string
  children: React.ReactNode
}) {
  return (
    <section>
      <h2 className="font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
        {title}
      </h2>
      {note && (
        <p className="mt-0.5 mb-1.5 text-[11px] text-muted-foreground/70 leading-snug">
          {note}
        </p>
      )}
      {children}
    </section>
  )
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between rounded border border-border/60 px-2 py-1 text-[12px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  )
}

/**
 * A preset drawn as a SHAPE, not as a picture of specific panels.
 *
 * The old thumbnail could show you agent-beside-canvas because a preset carried
 * its own content. A preset is now an arrangement applied to whatever you have
 * open, so the honest drawing is three blocks in a row, three in a column, or
 * one — and no labels, because it does not know what will be in them.
 */
export function ShapeThumbnail({
  shape,
  className = '',
}: {
  shape: ArrangeShape
  className?: string
}) {
  const blocks = shape === 'single' ? 1 : 3
  return (
    <span
      aria-hidden
      className={`flex h-6 w-9 shrink-0 gap-[2px] rounded-[3px] border border-border/70 p-[2px] ${
        shape === 'column' ? 'flex-col' : 'flex-row'
      } ${className}`}
    >
      {Array.from({ length: blocks }, (_, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length decoration
          key={i}
          className="flex-1 rounded-[2px] bg-muted-foreground/25"
        />
      ))}
    </span>
  )
}

/**
 * One app's settings (RFC 0284 S6g).
 *
 * A CONTROL PER DECLARED TYPE, and only three types exist because three is what
 * a 288px panel can draw honestly. An app wanting a fourth is asking for a
 * widget nobody has built, and a settings surface that renders a JSON blob for
 * anything it does not recognise is worse than refusing the type at install.
 */
function AppSettingsSectionView({
  section,
  onSet,
}: {
  section: AppSettingsSection
  onSet?: (appId: string, key: string, value: SettingValue | undefined) => void
}) {
  return (
    <Section title={section.name} note="Settings for this installed app.">
      <div className="space-y-1">
        {section.fields.map((field) => {
          // THE DEFAULT IS THE PLACEHOLDER, not the value. A field showing its
          // default as if somebody had typed it makes "back to the default"
          // unreachable, because clearing it would look like a change.
          const current = field.value
          if (field.type === 'boolean') {
            const on =
              typeof current === 'boolean' ? current : field.default === true
            return (
              <button
                key={field.key}
                type="button"
                onClick={() => onSet?.(section.appId, field.key, !on)}
                className="flex w-full items-baseline justify-between gap-2 rounded border border-border/60 px-2 py-1 text-[12px]"
              >
                <span className="text-muted-foreground">{field.label}</span>
                <span className="text-foreground">{on ? 'On' : 'Off'}</span>
              </button>
            )
          }
          return (
            <label
              key={field.key}
              className="flex items-baseline justify-between gap-2 rounded border border-border/60 px-2 py-1 text-[12px]"
            >
              <span className="min-w-0 truncate text-muted-foreground">
                {field.label}
              </span>
              <input
                type={field.type === 'number' ? 'number' : 'text'}
                value={current === undefined ? '' : String(current)}
                placeholder={
                  field.default === undefined ? '' : String(field.default)
                }
                onChange={(e) => {
                  const raw = e.target.value
                  if (raw.trim() === '') {
                    // UNDEFINED DELETES, which is how a setting goes back to
                    // its declared default rather than to an empty string.
                    onSet?.(section.appId, field.key, undefined)
                    return
                  }
                  const value = field.type === 'number' ? Number(raw) : raw
                  if (field.type === 'number' && Number.isNaN(value)) return
                  onSet?.(section.appId, field.key, value)
                }}
                className="w-24 shrink-0 rounded border border-border/60 bg-transparent px-1 text-right text-foreground"
              />
            </label>
          )
        })}
      </div>
      {section.fields.some((f) => f.description) && (
        <div className="mt-1 space-y-0.5">
          {section.fields
            .filter((f) => f.description)
            .map((f) => (
              <p
                key={f.key}
                className="px-2 text-[11px] text-muted-foreground leading-snug"
              >
                <span className="text-foreground">{f.label}.</span>{' '}
                {f.description}
              </p>
            ))}
        </div>
      )}
    </Section>
  )
}

/**
 * One Mod skill, its text shown in place (RFC 0334 3g). A Mod skill ships in
 * core and has no document, so it is read here rather than opened; to change
 * one, write a World or project skill with its id.
 */
function ModSkillRow({ skill }: { skill: ModSkill }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded border border-border/60">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full flex-col px-2 py-1 text-left hover:bg-muted/40"
      >
        <span className="text-[12px] text-foreground">
          {skill.title}{' '}
          <span className="font-mono text-[10px] text-muted-foreground/60">
            {skill.skillId}
          </span>
        </span>
        <span className="text-[11px] text-muted-foreground leading-snug">
          {skill.description}
        </span>
      </button>
      {open && skill.text && (
        <pre className="whitespace-pre-wrap border-border/60 border-t px-2 py-1.5 font-sans text-[11px] text-foreground/90 leading-snug">
          {skill.text}
        </pre>
      )}
    </div>
  )
}
